import { useContext, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
    getShipDefinition,
    type Planet,
    type PlayerRace,
    type ScienceId,
    type ShipCategory,
    type ShipDefinition,
    type ShipId,
} from '@kaeliux/shared';
import {
    calculateShipBuildCost,
    calculateShipBuildTime,
    extractBuildingLevels,
    getUnmetRequirements,
    getUpgradedShipStats,
} from '@kaeliux/game-engine';
import UnmetRequirementsGrid, { unmetRequirementTile, type RequirementTile } from './UnmetRequirementsGrid';
import BuildableThumb from './BuildableThumb';
import ResourceQuantities from './ResourceQuantities';
import ShipStats from './ShipStats';
import { Button } from './ui/Button';
import { Chip } from './ui/Chip';
import { Panel } from './ui/Panel';
import { FIELD_BASE_CLASSES, FIELD_FOCUS_CLASSES } from './ui/fieldStyles';
import { TeamPlanetViewContext } from '../contexts/TeamPlanetViewContext';
import { getShipIcon } from '../utils/shipIcons';
import { formatDuration } from '../utils/formatters';
import { cn } from '../utils/cn';

export const SHIP_CATEGORY_ORDER: ShipCategory[] = ['service', 'civilian', 'war'];

export const SHIP_CATEGORY_TITLES: Record<ShipCategory, string> = {
    service: 'Service ships',
    civilian: 'Civilian ships',
    war: 'War ships',
};

export interface ShipCardProps {
    def: ShipDefinition;
    planet: Planet;
    race: PlayerRace;
    scienceLevels: Partial<Record<ScienceId, number>>;
    buildingLevels: ReturnType<typeof extractBuildingLevels>;
    freePopulation: number;
    speedMultiplier: number;
    upgradeLevel: number;
    /** Whether "(upgrade Level N)" links to the Tech Center. Off where the Tech Center would show a different race. */
    upgradeLinked?: boolean;
    /** Queues `quantity` ships. Rejects with the server's reason on failure. */
    onBuild: (quantity: number) => Promise<void>;
    /** Robots-only (issue #91): when provided, renders a "+ Group" button that adds this ship/quantity to the page's delivery-group cart instead of building it immediately. */
    onAddToGroup?: (shipId: ShipId, quantity: number) => void;
    /** Replaces the quantity/Build controls with this explanation, e.g. another race's shipyard on the Team planet. */
    readOnlyReason?: string | null;
}

/**
 * One ship's card: stats, cost, build time, prerequisites and the Build
 * action. Shared by a personal planet's Factory and the Team planet's three
 * race shipyards, which differ only in where ships are queued.
 */
export default function ShipCard({
    def,
    planet,
    race,
    scienceLevels,
    buildingLevels,
    freePopulation,
    speedMultiplier,
    upgradeLevel,
    upgradeLinked = true,
    onBuild,
    onAddToGroup,
    readOnlyReason,
}: ShipCardProps) {
    const starportRoute = useContext(TeamPlanetViewContext)?.screenRoute('techCenter') ?? '/starport';
    const [quantity, setQuantity] = useState(1);
    const [isRequesting, setIsRequesting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const unmetRequirements = getUnmetRequirements(def.requirements, buildingLevels, scienceLevels);
    const upgradedStats = getUpgradedShipStats(def, upgradeLevel);
    const canPrereq = unmetRequirements.length === 0;
    const { perUnitSeconds, totalSeconds } = calculateShipBuildTime(
        race, def.id, quantity,
        buildingLevels.shipyard, buildingLevels.automationCenter, speedMultiplier,
    );
    const batchCost = calculateShipBuildCost(race, def.id, quantity);

    // Axon mutation: donors are consumed at queue time, so their hangar
    // slots come free before the population check — mirror the engine.
    const mutation = def.mutatesFrom;
    const donorDef = mutation ? getShipDefinition(race, mutation.shipId) : null;
    const donorsNeeded = mutation ? mutation.quantity * quantity : 0;
    const dockedDonors = mutation ? (planet.ships?.[mutation.shipId] ?? 0) : 0;
    const donorsOk = !mutation || dockedDonors >= donorsNeeded;
    const popRelief = donorDef ? donorDef.hangar * donorsNeeded : 0;

    const requirementTiles: RequirementTile[] = unmetRequirements.map((req) => unmetRequirementTile(req, race));
    if (mutation && donorDef && !donorsOk) {
        requirementTiles.push({
            key: `mutation-${donorDef.id}`,
            name: donorDef.name,
            sublabel: `${dockedDonors.toLocaleString()} / ${donorsNeeded.toLocaleString()} docked`,
            image: getShipIcon(race, donorDef.id),
            onClick: () => {
                document.getElementById(`ship-${donorDef.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            },
        });
    }

    const popNeeded = def.hangar * quantity;
    const popFits = popNeeded <= freePopulation + popRelief;
    const affordable =
        (batchCost.metal ?? 0) <= planet.resources.metal.current &&
        (batchCost.minerals ?? 0) <= planet.resources.minerals.current &&
        (batchCost.gas ?? 0) <= planet.resources.gas.current;

    const disabled = !canPrereq || !donorsOk || !popFits || !affordable || isRequesting;
    const disabledReason: ReactNode = !canPrereq
        ? null // shown as the full-width UnmetRequirementsGrid below instead
        : !donorsOk
            ? `Not enough docked ${donorDef?.name ?? 'donor ships'}`
            : !popFits
                ? 'Not enough free population'
                : !affordable
                    ? 'Insufficient resources'
                    : null;

    async function handleBuild() {
        setIsRequesting(true);
        setError(null);
        try {
            await onBuild(quantity);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Build failed');
        } finally {
            setIsRequesting(false);
        }
    }

    const docked = planet.ships?.[def.id] ?? 0;

    const shipImage = (
        <div className="w-full h-full rounded-tile border border-hairline bg-surface-void/60 flex items-center justify-center overflow-hidden">
            <img
                src={getShipIcon(race, def.id)}
                alt={def.name}
                className="w-full h-full object-contain"
            />
        </div>
    );

    return (
        <Panel
            variant="solid"
            id={`ship-${def.id}`}
            data-testid="ship-card"
            className="flex flex-col gap-3"
        >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex-1 min-w-0 flex flex-col sm:flex-row items-start gap-3">
                    <BuildableThumb
                        image={shipImage}
                        countBadge={docked}
                        countTitle={`Docked: ${docked}`}
                        cost={def.cost}
                        costLabel="Per unit"
                        imageBoxClassName="w-32 sm:w-36 aspect-[4/3]"
                    />
                    <div className="min-w-0">
                        <div className="flex items-baseline gap-2">
                            <h3 className="text-title text-content-primary">{def.name}</h3>
                            <span className="text-body text-content-muted">Docked: {docked.toLocaleString()}</span>
                        </div>
                        <p className="text-body text-content-muted">{def.description}</p>
                        <p className="text-micro text-content-muted mt-1">
                            <ShipStats stats={upgradedStats} hangar={def.hangar} />
                            {upgradeLevel > 0 && (upgradeLinked ? (
                                <Link to={starportRoute} className="text-content-muted hover:text-content-primary underline ml-1">
                                    (upgrade Level {upgradeLevel})
                                </Link>
                            ) : (
                                <span className="text-content-muted ml-1">(upgrade Level {upgradeLevel})</span>
                            ))}
                        </p>
                        {mutation && donorDef && (
                            <p className="text-micro text-content-muted mt-1">
                                Mutates from {mutation.quantity} × {donorDef.name} per unit · {dockedDonors.toLocaleString()} docked
                            </p>
                        )}
                        <p className="text-body text-content-secondary mt-2">
                            <span className="text-content-muted mr-2">Build time:</span>
                            {formatDuration(perUnitSeconds)}
                        </p>
                        {quantity > 1 && (
                            <p className="text-micro text-content-muted mt-1">
                                Total × {quantity}: <ResourceQuantities amounts={batchCost} /> · {formatDuration(totalSeconds)}
                            </p>
                        )}
                        {error && <Chip tone="danger" className="mt-1" role="alert">{error}</Chip>}
                    </div>
                </div>
                {readOnlyReason ? (
                    <span className="text-label text-content-muted sm:max-w-[14rem] sm:text-right">{readOnlyReason}</span>
                ) : (
                    <div className="flex flex-col items-start sm:items-end gap-1">
                        <div className="flex gap-2 items-center">
                            {/* Plain input, not <NumberStepper> — its "Decrease
                                {label}"/"Increase {label}" button names would
                                substring-collide with this field's own aria-label
                                under Playwright's getByLabel, which shipyard.spec.ts
                                (frozen) relies on resolving to exactly one element. */}
                            <input
                                type="number"
                                min={1}
                                step={1}
                                value={quantity}
                                onChange={(e) => setQuantity(Math.max(1, Number.parseInt(e.target.value, 10) || 1))}
                                aria-label={`${def.name} quantity`}
                                className={cn(FIELD_BASE_CLASSES, FIELD_FOCUS_CLASSES, 'w-20 text-center')}
                            />
                            <Button
                                type="button"
                                variant="primary"
                                size="md"
                                onClick={handleBuild}
                                disabled={disabled}
                            >
                                {isRequesting ? 'Queuing…' : 'Build'}
                            </Button>
                            {onAddToGroup && (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="md"
                                    onClick={() => onAddToGroup(def.id, quantity)}
                                    // Same gate as Build: staging something the
                                    // server will reject only surfaces the error at
                                    // submit, where it fails the whole group.
                                    disabled={disabled}
                                    title="Add to a delivery group instead of building immediately"
                                >
                                    + Group
                                </Button>
                            )}
                        </div>
                        {disabledReason && !isRequesting && (
                            <Chip tone="danger">{disabledReason}</Chip>
                        )}
                    </div>
                )}
            </div>
            {requirementTiles.length > 0 && !isRequesting && (
                <UnmetRequirementsGrid tiles={requirementTiles} />
            )}
        </Panel>
    );
}
